import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { DSCard, DSPageHeader, DSIcon, DSSelect } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './SquareSyncHistory.css';

function SquareSyncHistory() {
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedSync, setExpandedSync] = useState(null);

  const loadSyncHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filterType) {
        params.syncType = filterType;
      }

      const response = await apiService.get('/square/sync/history', { params });
      setSyncHistory(response.data);
    } catch (err) {
      setError('Failed to load sync history');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadSyncHistory();
  }, [loadSyncHistory]);

  const toggleExpanded = (syncId) => {
    setExpandedSync(expandedSync === syncId ? null : syncId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusBadge = (status) => {
    const variant = status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'warning';
    return <StatusBadge variant={variant} uppercase>{status}</StatusBadge>;
  };

  const getSyncTypeLabel = (syncType) => {
    const labels = {
      'subscriptions': 'Subscriptions',
      'invoices': 'Invoices',
      'orders': 'Orders',
      'customers': 'Customers',
      'full': 'Full Sync'
    };
    return labels[syncType] || syncType;
  };

  if (loading) {
    return (
      <div className="sync-history-page">
        <div className="loading">Loading sync history...</div>
      </div>
    );
  }

  return (
    <div className="sync-history-page">
      <DSPageHeader
        title="Square Sync History"
        actions={(
          <div className="header-actions">
            <DSSelect
              className="filter-select"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="">All Sync Types</option>
              <option value="subscriptions">Subscriptions</option>
              <option value="invoices">Invoices</option>
              <option value="orders">Orders</option>
              <option value="customers">Customers</option>
              <option value="full">Full Sync</option>
            </DSSelect>
            <button className="btn-refresh" onClick={loadSyncHistory}>
              Refresh
            </button>
          </div>
        )}
      />

      {error && <div className="error-message">{error}</div>}

      {syncHistory.length === 0 ? (
        <div className="no-items">No sync history found</div>
      ) : (
        <div className="sync-list">
          {syncHistory.map(sync => (
            <DSCard key={sync.id} className={`sync-card ${sync.status}`} cornerVariant="md">
              <div className="sync-header" onClick={() => toggleExpanded(sync.id)}>
                <div className="sync-main-info">
                  <div className="sync-type">
                    {getSyncTypeLabel(sync.sync_type)}
                  </div>
                  <div className="sync-time">
                    {formatDate(sync.started_at)}
                  </div>
                </div>
                
                <div className="sync-stats">
                  {getStatusBadge(sync.status)}
                  <span className="stat">
                    <strong>{sync.records_processed || 0}</strong> processed
                  </span>
                  {sync.duplicates_skipped > 0 && (
                    <span className="stat warning">
                      <strong>{sync.duplicates_skipped}</strong> duplicates
                    </span>
                  )}
                  {sync.split_payments_created > 0 && (
                    <span className="stat">
                      <strong>{sync.split_payments_created}</strong> splits
                    </span>
                  )}
                  {sync.errors_encountered > 0 && (
                    <span className="stat error">
                      <strong>{sync.errors_encountered}</strong> errors
                    </span>
                  )}
                </div>

                <div className="sync-toggle">
                  {expandedSync === sync.id ? <DSIcon name="chevronDown" /> : <DSIcon name="chevronRight" />}
                </div>
              </div>

              {expandedSync === sync.id && (
                <div className="sync-details">
                  <div className="details-grid">
                    <div className="detail-item">
                      <strong>Sync ID:</strong>
                      <span>{sync.id}</span>
                    </div>
                    
                    <div className="detail-item">
                      <strong>Started:</strong>
                      <span>{formatDate(sync.started_at)}</span>
                    </div>
                    
                    {sync.completed_at && (
                      <>
                        <div className="detail-item">
                          <strong>Completed:</strong>
                          <span>{formatDate(sync.completed_at)}</span>
                        </div>
                        
                        <div className="detail-item">
                          <strong>Duration:</strong>
                          <span>{formatDuration(sync.started_at, sync.completed_at)}</span>
                        </div>
                      </>
                    )}
                    
                    {sync.user_id && (
                      <div className="detail-item">
                        <strong>User ID:</strong>
                        <span>{sync.user_id}</span>
                      </div>
                    )}
                    
                    {sync.triggered_by && (
                      <div className="detail-item">
                        <strong>Triggered By:</strong>
                        <span>{sync.triggered_by}</span>
                      </div>
                    )}
                  </div>

                  {sync.error_details && (
                    <div className="error-details">
                      <strong>Errors during sync:</strong>
                      <pre>{typeof sync.error_details === 'string' 
                        ? JSON.stringify(JSON.parse(sync.error_details), null, 2)
                        : JSON.stringify(sync.error_details, null, 2)
                      }</pre>
                    </div>
                  )}

                  <div className="sync-summary">
                    <h4>Summary</h4>
                    <ul>
                      <li>{sync.records_processed || 0} records processed</li>
                      {sync.duplicates_skipped > 0 && (
                        <li>{sync.duplicates_skipped} duplicate payments skipped</li>
                      )}
                      {sync.split_payments_created > 0 && (
                        <li>{sync.split_payments_created} multi-month payments split</li>
                      )}
                      {sync.errors_encountered > 0 && (
                        <li className="error-item">{sync.errors_encountered} errors encountered</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </DSCard>
          ))}
        </div>
      )}
    </div>
  );
}

export default SquareSyncHistory;
